'use client';
import {
  QuestionStudyActivitySubType,
  ShortAnswerWordHint,
  StudyActivityMetaDataUnionType,
  StudyActivityTypes,
  StudyActivityTypesEnum,
} from '@mildang/graphql-artifact';
import { css } from '@emotion/react';
import '@mildang/mui';
import { StudyModuleCard_StudyModuleInstanceFragment } from '@ui/components/student/pattern/StudyModuleCard/StudyModuleCard.generated';
import { Theme } from '@mildang/mui';
import '@mildang/mui';
import { ReactNode, createContext } from 'react';
import { workbookActivityNodeNames } from '@ui/components/molecules/RichEditor/custom-nodes/constants';
import { SubmissionPhase } from '../constants';
import { StudyActivitiesTable_StudyActivityFragment } from '@ui/components/patterns/StudyActivitiesTable/StudyActivitiesTable.generated';
import { STUDY_ACTIVITY_SUB_TYPE_LABELS } from '@shared/constants/studyActivityType';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { I18n } from '@lingui/core';

export const getStudyActivityTypeLabels: (i18n: I18n) => Record<StudyActivityTypes, string> = (i18n) => ({
  ['QUESTION']: t(i18n)`문제`,
  ['READING']: t(i18n)`독해`,
  ['VIDEO']: t(i18n)`강의`,
  ['MEGASTUDY_VIDEO']: t(i18n)`강의`,
  ['WORD_TEST']: t(i18n)`단어시험`,
  ['WORD_MEMORY']: t(i18n)`단어암기`,
  ['MATH_QUESTION']: t(i18n)`수학 문제`,
  ['TODO_LIST']: t(i18n)`학습 목표`,
  ['ENGLISH_SPEAKING']: t(i18n)`말하기`,

  //단계별 문장암기SA 칩은 모두 '암기'로 보여준다.
  ['SENTENCE_COMPLETE_BLOCKS']: t(i18n)`암기`,
  ['SENTENCE_MATCH_WHOLE_BLOCKS']: t(i18n)`암기`,
  ['SENTENCE_MATCH_BLANK_BLOCKS']: t(i18n)`암기`,
  ['SENTENCE_COMPLETE_WHOLE_BLOCKS']: t(i18n)`암기`,
  ['SENTENCE_GRAMMAR_STUDY']: t(i18n)`암기`,
  ['SENTENCE_ORDER']: t(i18n)`순서`,
  ['ARTICLE']: t(i18n)`자료`,
});

export const STUDY_ACTIVITY_TYPE_FILTER_LABELS: Record<
  Exclude<StudyActivityTypesEnum, StudyActivityTypesEnum.TODO_LIST>,
  ReactNode
> = {
  ['QUESTION']: <Trans>영어 문제</Trans>,
  ['MATH_QUESTION']: <Trans>수학 문제</Trans>,
  ['VIDEO']: <Trans>강의</Trans>,
  ['MEGASTUDY_VIDEO']: <Trans>강의</Trans>,
  ['SENTENCE_ORDER']: <Trans>순서</Trans>,
  ['SENTENCE_MATCH_BLANK_BLOCKS']: <Trans>1단계 - 빈칸 블록 맞추기</Trans>,
  ['SENTENCE_MATCH_WHOLE_BLOCKS']: <Trans>2단계 - 문장 블록 맞추기</Trans>,
  ['SENTENCE_COMPLETE_BLOCKS']: <Trans>3단계 - 빈칸 완성하기</Trans>,
  ['SENTENCE_COMPLETE_WHOLE_BLOCKS']: <Trans>4단계 - 문장 완성하기</Trans>,
  ['SENTENCE_GRAMMAR_STUDY']: <Trans>5단계 - 중요 문법 확인하기</Trans>,
  ['READING']: <Trans>문장 독해</Trans>,
  ['WORD_MEMORY']: <Trans>단어 암기</Trans>,
  ['WORD_TEST']: <Trans>단어 시험</Trans>,
  //TODO: 학습 목표 라벨 바꾸기
  // [StudyActivityTypes.TodoList]: '학습 목표',
  ['ARTICLE']: <Trans>자료</Trans>,
  ['ENGLISH_SPEAKING']: <Trans>말하기</Trans>,
};

export const WORD_LEARNING_STATE_TYPE_LABEL: Record<string, ReactNode> = {
  KNOW: <Trans>이미 아는 단어</Trans>,
  UNKNOWN: <Trans>모르는 단어</Trans>,
  NEW: <Trans>0회 학습</Trans>,
};

export const wordTestDialogStyle = css`
  .${'MuiDialog-paperWidthXs'} {
    max-width: 312px;
  }
`;

export const TestStudyModules: StudyModuleCard_StudyModuleInstanceFragment[] = [
  {
    id: 'test',
    isStudyModuleInstanceComplete: false,
    studyModuleInstanceCompleteRate: 50,
    feed: {
      type: 'STUDY_MODULE',
    },
    studyUnitInstances: [
      {
        id: 'test1',
        isEstimative: false,
        viewDatas: [],
        studyActivityInstances: [
          { id: 'test1', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test2', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test3', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test4', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test5', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test6', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test7', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test8', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test9', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test10', type: 'VIDEO', title: 'test', isRequired: true },
        ],
      },
      {
        id: 'test2',
        isEstimative: true,
        viewDatas: [],
        studyActivityInstances: [
          { id: 'test1', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test2', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test3', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test4', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test5', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test6', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test7', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test8', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test9', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test10', type: 'VIDEO', title: 'test', isRequired: true },
        ],
      },
    ],
  },
  {
    id: 'test2',
    isStudyModuleInstanceComplete: true,
    studyModuleInstanceCompleteRate: 100,
    feed: {
      type: 'WRONG_ANSWER',
    },
    studyUnitInstances: [
      {
        id: 'test1',
        isEstimative: false,
        viewDatas: [],
        studyActivityInstances: [
          { id: 'test1', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test2', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test3', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test4', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test5', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test6', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test7', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test8', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test9', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test10', type: 'VIDEO', title: 'test', isRequired: true },
        ],
      },
      {
        id: 'test2',
        isEstimative: false,
        viewDatas: [],

        studyActivityInstances: [
          { id: 'test1', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test2', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test3', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test4', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test5', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test6', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test7', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test8', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test9', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test10', type: 'VIDEO', title: 'test', isRequired: true },
        ],
      },
    ],
  },
  {
    id: 'test3',
    isStudyModuleInstanceComplete: false,
    studyModuleInstanceCompleteRate: 0,
    feed: {
      type: 'WRONG_ANSWER',
    },
    studyUnitInstances: [
      {
        id: 'test1',
        isEstimative: false,
        viewDatas: [],
        studyActivityInstances: [
          { id: 'test1', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test2', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test3', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test4', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test5', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test6', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test7', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test8', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test9', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test10', type: 'VIDEO', title: 'test', isRequired: true },
        ],
      },
      {
        id: 'test2',
        isEstimative: false,
        viewDatas: [],
        studyActivityInstances: [
          { id: 'test1', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test2', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test3', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test4', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test5', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test6', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test7', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test8', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test9', type: 'VIDEO', title: 'test', isRequired: true },
          { id: 'test10', type: 'VIDEO', title: 'test', isRequired: true },
        ],
      },
    ],
  },
];

export const SMICompleteRateProgressBarStyle = (theme: Theme, completeRate: number) => {
  let bgColor = theme.palette.stroke;
  let barColor = theme.palette.secondary.main;

  if (completeRate > 0 && completeRate < 70) {
    bgColor = theme.palette.warning.lightActive;
    barColor = theme.palette.warning.main;
  }

  return css`
    height: 4px;
    background-color: ${bgColor};

    & .${'MuiLinearProgress-bar'} {
      background-color: ${barColor};
    }
  `;
};

export const WorkbookActivityContext = createContext<{
  /** submitted answers */
  studentAnswers: string[];
  /** answers on client, ready to submit*/
  pendingAnswers?: string[];
  onUpdate: ({
    nodeIndex,
    answer,
    selectedText,
  }: {
    /** referralTarget activity doesn't need a nodeIndex : it uses internal nodes' ids*/
    nodeIndex?: number;
    /** to inform which was the previously selected one */
    selectedText?: string;
    answer: string;
  }) => void;
  wordHint?: ShortAnswerWordHint | null;
  hideOption?: boolean | null;
  readOnly: boolean;
  submissionPhase?: SubmissionPhase;
  hintView?: boolean;
  submitted?: boolean;
  hideResult?: boolean;
  isPreviewInCMS?: boolean;
}>({
  studentAnswers: [],
  pendingAnswers: [],
  onUpdate: () => {},
  wordHint: null,
  hideOption: null,
  readOnly: false,
  submissionPhase: 'UNSOLVED',
  hintView: false,
  hideResult: false,
  isPreviewInCMS: false,
});

export const findQuizNodes = (nodes) => {
  let acc: any[] = [];
  if (!nodes) return acc;
  for (const node of nodes) {
    if (Object.values(workbookActivityNodeNames).includes(node.type)) {
      acc.push(node);
    }
    if (node.content) acc = [...acc, ...findQuizNodes(node.content)];
  }
  return acc;
};

export const getWorkbookHelpText = ({
  subType,
  shouldShowPartialCheckGuide = false,
  hideOption = false,
}: {
  subType?: QuestionStudyActivitySubType | null;
  shouldShowPartialCheckGuide?: boolean;
  hideOption?: boolean | null;
}) => {
  switch (subType) {
    case 'COMMON':
      return null;
    case 'SELECTING_GRAMMAR_OR_VOCABULARY':
      if (hideOption) return <Trans>단어를 드래그하거나 더블클릭하면 수정하기 버튼이 나타나요.</Trans>;
      return <Trans>괄호 안의 단어를 선택해서 답을 골라 주세요.</Trans>;
    case 'FILLING_BLANK_TYPING':
      return <Trans>빈칸을 선택하면 답을 입력할 수 있어요.</Trans>;
    case 'FILLING_BLANK_CHOICE':
      return (
        <Trans>
          빈칸이 여러 개면 블록을 빈칸의 순서대로 선택해 주세요. 취소를 원하면 선택한 블록을 다시 선택해
          주세요.
        </Trans>
      );
    case 'FINDING_REFERRAL_TARGET':
      if (shouldShowPartialCheckGuide)
        return <Trans>부분 정답 옵션이 적용되어 1개만 맞춰도 정답이예요.</Trans>;
      return (
        <Trans>
          두 단어 이상이 정답인 경우 해당되는 모든 단어를 선택하세요. 취소를 원하면 단어를 다시 선택하세요.
        </Trans>
      );
  }
};
export const getWorkbookA11yHelpText = ({
  subType,
  shouldShowPartialCheckGuide = false,
  hideOption = false,
}: {
  subType?: QuestionStudyActivitySubType | null;
  shouldShowPartialCheckGuide?: boolean;
  hideOption?: boolean | null;
}) => {
  switch (subType) {
    case 'COMMON':
      return null;
    case 'SELECTING_GRAMMAR_OR_VOCABULARY':
      if (hideOption) return <Trans>단어를 드래그하거나 더블클릭하면 수정하기 버튼이 나타나요.</Trans>;
      return <Trans>선택지 안에서 답을 골라 주세요.</Trans>;
    case 'FILLING_BLANK_TYPING':
      return <Trans>빈칸을 선택하면 답을 입력할 수 있어요.</Trans>;

    case 'FILLING_BLANK_CHOICE':
      return <Trans>사용 방법: 아래 문장 중간에 있는 빈칸에서 정답을 선택해 주세요.</Trans>;
    case 'FINDING_REFERRAL_TARGET':
      if (shouldShowPartialCheckGuide)
        return <Trans>부분 정답 옵션이 적용되어 1개만 맞춰도 정답이예요.</Trans>;
      return (
        <Trans>
          두 단어 이상이 정답인 경우 해당되는 모든 단어를 선택하세요. 취소를 원하면 단어를 다시 선택하세요.
        </Trans>
      );
  }
};

type Metadata = StudyActivitiesTable_StudyActivityFragment['metadata'] | StudyActivityMetaDataUnionType;

export const getSubTypeDifficultyLabel = (metadata: Metadata) => {
  const labels: string[] = [];
  if (
    metadata.__typename === 'StudyActivityQuestion' ||
    metadata.__typename === 'StudyActivityMathQuestion'
  ) {
    if (metadata?.subType) labels.push(STUDY_ACTIVITY_SUB_TYPE_LABELS[metadata?.subType]);
    if (metadata?.subType === 'FILLING_BLANK_CHOICE') {
      if (metadata.subQuestions?.[0].__typename === 'StudyActivityShortAnswerSubQuestion') {
        if (metadata.__typename === 'StudyActivityQuestion' && metadata.subQuestions?.[0].hideTranslation)
          labels.push('해석 숨기기');
      }
    }
    if (metadata?.subType === 'FINDING_REFERRAL_TARGET') {
      if (metadata.subQuestions?.[0].__typename === 'StudyActivityReferralTargetSubQuestion') {
        if (metadata.__typename === 'StudyActivityQuestion' && metadata.subQuestions?.[0].hideTranslation)
          labels.push('해석 숨기기');
      }
    }
    if (metadata?.subType === 'FILLING_BLANK_TYPING') {
      if (metadata.subQuestions?.[0].__typename === 'StudyActivityShortAnswerSubQuestion') {
        if (metadata.subQuestions?.[0].wordHint === 'SHOW_WORD_COUNT') labels.push('총 단어 수 보여주기');
        if (metadata.subQuestions[0].wordHint === 'SHOW_WRONG_WORD') labels.push('오답 단어 표시하기');
        if (metadata.__typename === 'StudyActivityQuestion' && metadata.subQuestions?.[0].hideTranslation)
          labels.push('해석 숨기기');
      }
    }
    if (metadata?.subType === 'SELECTING_GRAMMAR_OR_VOCABULARY') {
      if (metadata.subQuestions?.[0].__typename === 'StudyActivitySelectingGrammarOrVocabularySubQuestion') {
        if (metadata.subQuestions?.[0].hideOption) labels.push('선택지 숨기기');
        if (metadata.__typename === 'StudyActivityQuestion' && metadata.subQuestions?.[0].hideTranslation)
          labels.push('해석 숨기기');
      }
    }
  }
  return labels;
};
